import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { localDateKey } from "@/lib/time";
import { cn } from "@/lib/utils";

// Dates travel as "YYYY-MM-DD" strings (the same value a native date input gave).
// Parse and format by components so a timezone never shifts the day.
function parseKey(key: string): Date | undefined {
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
	if (!m) return undefined;
	const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	return Number.isNaN(d.getTime()) ? undefined : d;
}

function toKey(d: Date): string {
	const p = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

type DatePickerProps = {
	value: string;
	onChange: (value: string) => void;
	id?: string;
	placeholder?: string;
	/** Latest selectable day, "YYYY-MM-DD". */
	max?: string;
	/** Show a Clear action; for optional dates. */
	clearable?: boolean;
	className?: string;
	disabled?: boolean;
	"aria-label"?: string;
};

export function DatePicker({
	value,
	onChange,
	id,
	placeholder = "Pick a date",
	max,
	clearable,
	className,
	disabled,
	"aria-label": ariaLabel,
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);
	const date = parseKey(value);
	return (
		<Popover modal open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="outline"
					disabled={disabled}
					aria-label={ariaLabel}
					className={cn(
						"h-11 w-full justify-start px-3 text-left font-normal md:h-9",
						!date && "text-muted-foreground",
						className,
					)}
				>
					<CalendarIcon className="size-4 opacity-70" />
					{date ? format(date, "EEE d MMM yyyy") : placeholder}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-2" align="start">
				<Calendar
					mode="single"
					selected={date}
					defaultMonth={date}
					disabled={max ? { after: parseKey(max) ?? new Date(8.64e15) } : undefined}
					onSelect={(d) => {
						if (d) onChange(toKey(d));
						setOpen(false);
					}}
				/>
				<div className="flex justify-between px-1 pt-1">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => {
							onChange(localDateKey());
							setOpen(false);
						}}
					>
						Today
					</Button>
					{clearable && value && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => {
								onChange("");
								setOpen(false);
							}}
						>
							Clear
						</Button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
